import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, ConfirmationComponent, Pager, Tabulation, TabulationService } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { TabulationModalComponent } from './tabulation-modal/tabulation-modal.component';

@Component({
    selector: 'app-tabulation',
    // templateUrl: './tabulation.component.html',
    templateUrl: './tabulation.v2.component.html',
    styleUrl: './tabulation.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabulationComponent extends AbstractComponent implements OnInit {

  pager: Pager<Tabulation> = new Pager<Tabulation>({ perPage: 10 });

  listObservable: Observable<Pager<Tabulation>>;
  private searchTerm: Subject<string> = new Subject<string>();

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public tabulationService: TabulationService,
  ) {
    super(injector);
    this.setupForm();
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.TABULATIONS_LIST;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<Tabulation>();
    if (!cached) {
      this.getList();
      return;
    }
    this.pager = cached.pager;
    this.searchString = cached.search ?? '';
    this.loadingContent = false;

    if (this.searchString) {
      this.formGroup.get('search').setValue(this.searchString);
    }

    this.setupObservableSearch();
  }


  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      search: [''],
    });
  }
  setupObservableSearch() {
    this.listObservable = this.searchTerm
      .pipe(takeUntil(this.destroy$))
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.tabulationService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      }));
    this.listObservable.subscribe((pager: Pager<Tabulation>) => {
      this.pager = pager;
      this.persistListState(this.pager, this.searchString, this.searchParams);
    });
  }

  getList() {
    this.loadingContent = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    this.tabulationService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Tabulation>) => {
          this.pager = pager;
          this.setupObservableSearch();
          this.persistListState(this.pager, this.searchString, this.searchParams);
        },
        complete: () => this.loadingContent = false,
        error: (error) => this.alertService.error(error.error.message)

      });
  }


  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo.trim();

    if (!termo.trim()) {
      this.searchString = '';
    }

    this.searchTerm.next(termo);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  add(): void {
    const dialogRef = this.dialog.open(TabulationModalComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result) this.getList();
    });
  }

  edit(tabulation: Tabulation): void {
    const dialogRef = this.dialog.open(TabulationModalComponent, {
      width: '600px',
      data: tabulation,
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result) this.getList();
    });
  }

  delete(tabulation: Tabulation): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.tabulationService.delete(tabulation._id).subscribe({
          next: (value) => {
            this.alertService.success(this.t('tabulation.list.deleteSuccess'));
            this.getList();
          },
          error: (error) => this.alertService.error(error.error.message),
        });
      }
    });
  }

}
